import React, { useState } from 'react';
import { Calculator, Users, MessageSquare, History, Settings, ArrowRight, X } from 'lucide-react';

interface OnboardingScreenProps {
  onComplete: () => void;
}

const OnboardingScreen: React.FC<OnboardingScreenProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to DeCalculator",
      subtitle: "Your collaborative calculator experience",
      content: (
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl flex items-center justify-center">
            <Calculator className="h-12 w-12 text-white" />
          </div>
          <p className="text-lg text-gray-300 mb-4">
            DeCalculator is a real-time collaborative calculator that lets you work together with others on complex calculations.
          </p>
        </div>
      )
    },
    {
      title: "Real-time Collaboration",
      subtitle: "Work together seamlessly",
      content: (
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-4 bg-dark-700 rounded-lg">
            <Users className="h-6 w-6 text-accent-500" />
            <div>
              <h3 className="font-semibold text-white">Live Sessions</h3>
              <p className="text-sm text-gray-400">See who's online and working with you</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-dark-700 rounded-lg">
            <MessageSquare className="h-6 w-6 text-accent-500" />
            <div>
              <h3 className="font-semibold text-white">Real-time Chat</h3>
              <p className="text-sm text-gray-400">Communicate while calculating</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-dark-700 rounded-lg">
            <History className="h-6 w-6 text-accent-500" />
            <div>
              <h3 className="font-semibold text-white">Shared History</h3>
              <p className="text-sm text-gray-400">All calculations are synced across users</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Advanced Features",
      subtitle: "More than just a calculator",
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-dark-700 rounded-lg text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-accent-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">x</span>
              </div>
              <h3 className="font-semibold text-white text-sm">Variables</h3>
              <p className="text-xs text-gray-400">Define and reuse values</p>
            </div>
            <div className="p-4 bg-dark-700 rounded-lg text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-accent-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">π</span>
              </div>
              <h3 className="font-semibold text-white text-sm">Scientific</h3>
              <p className="text-xs text-gray-400">Advanced math functions</p>
            </div>
            <div className="p-4 bg-dark-700 rounded-lg text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-accent-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">01</span>
              </div>
              <h3 className="font-semibold text-white text-sm">Programmer</h3>
              <p className="text-xs text-gray-400">Binary, hex, and more</p>
            </div>
            <div className="p-4 bg-dark-700 rounded-lg text-center">
              <Settings className="h-6 w-6 mx-auto mb-2 text-accent-500" />
              <h3 className="font-semibold text-white text-sm">Customizable</h3>
              <p className="text-xs text-gray-400">Personalize your experience</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Get Started",
      subtitle: "Ready to calculate together?",
      content: (
        <div className="text-center space-y-6">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center">
            <ArrowRight className="h-8 w-8 text-white" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-white">You're all set!</h3>
            <p className="text-gray-400">
              Create a session to start collaborating or join an existing one.
            </p>
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipOnboarding = () => {
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-dark-900 bg-opacity-95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-dark-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-accent-600 rounded-lg flex items-center justify-center">
              <Calculator className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">DeCalculator</h1>
              <p className="text-sm text-gray-400">Getting Started</p>
            </div>
          </div>
          <button
            onClick={skipOnboarding}
            className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">
              {steps[currentStep].title}
            </h2>
            <p className="text-gray-400">
              {steps[currentStep].subtitle}
            </p>
          </div>

          <div className="mb-8">
            {steps[currentStep].content}
          </div>

          {/* Progress */}
          <div className="flex justify-center mb-6">
            <div className="flex space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentStep ? 'bg-accent-500' : 'bg-dark-600'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                currentStep === 0
                  ? 'bg-dark-700 text-gray-500 cursor-not-allowed'
                  : 'bg-dark-700 text-white hover:bg-dark-600'
              }`}
            >
              Previous
            </button>
            
            <button
              onClick={nextStep}
              className="px-6 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <span>{currentStep === steps.length - 1 ? 'Get Started' : 'Next'}</span>
              {currentStep < steps.length - 1 && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingScreen;
